import React from 'react';
import { AlertTriangle, RotateCw, Search } from 'lucide-react';
import { Button } from '../ui';
import { ComponentPhase, LineKeySpec, SupplierView, shortPartName } from './discoveryModel';
import { discoveryLayoutOf, listingKeyOf, pickedSupplierCount } from './listingGroups';
import { alternativeLabelOf, specChipsOf, specSuggestionOf } from './specChipModel';
import { detailWaitLine } from './researchProgress';
import { MinuteRange, ResearchState } from '../../types/bom.types';
import SupplierTrustMatrix from './SupplierTrustMatrix';
import ResearchStages from './ResearchStages';
import LineSpecSummary from './LineSpecSummary';
import SpecChips from './SpecChips';
import SpecSuggestion from './SpecSuggestion';
import SpecPreviewBar from './SpecPreviewBar';
import SpecSupplierList from './SpecSupplierList';
import { useSpecPreview } from '../../hooks/useSpecPreview';
import { useReadOnly } from '../../contexts/WizardAccessContext';

/**
 * Discovery detail pane: the line's name and compact spec summary → the
 * "Specs found" chips (only when research found another spec) → at most one
 * Robbie suggestion → one supplier list for the chosen spec ("Robbie's picks"
 * #1/#2, then the other suppliers) → the Supplier Trust Matrix.
 *
 * Only the line's own spec is shortlistable. Choosing another spec's chip
 * previews its suppliers read-only, with one button that switches the line
 * (the existing spec-swap through the BOM edit); after the switch that spec
 * is the line's own and the old one becomes a chip.
 */
interface ComponentDetailPaneProps {
  name: string;
  partNumber?: string;
  mpn?: string;
  quantity?: number;
  description?: string;
  /** The line's key specs in wording order (server-read); [] → the description shows. */
  keySpecs?: LineKeySpec[];
  phase: ComponentPhase;
  /** Live research facts from the status poll. */
  researchState?: ResearchState;
  researchStage?: number | null;
  /** Known suppliers the pre-research network check found; null when not recorded. */
  knownSuppliers?: number | null;
  attempt?: number | null;
  maxAttempts?: number;
  startsInMinutes?: MinuteRange | null;
  suppliers: SupplierView[];
  /** Ticked product rows (listing keys). */
  tickedKeys: string[];
  onToggleListing: (listingKey: string) => void;
  /** The spec chip chosen when the pane opens ('' = the line's own); the /design gallery opens on a preview. */
  initialSpecGroup?: string;
  /** Switch the line to an alternative spec; absent → no switch offered. */
  onUseAlternative?: (specGroup: string) => void;
  switchingSpec?: string | null;
  onRetry: () => void;
  isRetrying: boolean;
  /** Buyer requirements that power computed context; absent → context omitted. */
  targetPrice?: number;
  leadWindowWeeks?: number;
  requiredCompliance?: string[];
  /** Queues a worth-asking note for the supplier's first outreach message. */
  onAddToFirstMessage?: (supplierName: string, note: string) => void;
}

const CenteredState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-s4 p-[24px] text-center">{children}</div>
);

export const ComponentDetailPane: React.FC<ComponentDetailPaneProps> = ({
  name,
  partNumber,
  mpn,
  quantity,
  description,
  keySpecs = [],
  phase,
  researchState,
  researchStage,
  knownSuppliers,
  attempt,
  maxAttempts,
  startsInMinutes,
  suppliers,
  tickedKeys,
  onToggleListing,
  initialSpecGroup = '',
  onUseAlternative,
  switchingSpec,
  onRetry,
  isRetrying,
  targetPrice,
  leadWindowWeeks,
  requiredCompliance,
  onAddToFirstMessage,
}) => {
  const readOnly = useReadOnly();
  const layout = discoveryLayoutOf(suppliers);
  const { main, alternatives } = layout;
  const lineSignature = `${name}|${description ?? ''}|${keySpecs.map((k) => k.text).join(' ')}`;
  const view = useSpecPreview(alternatives, lineSignature, initialSpecGroup);
  const chips = specChipsOf(layout);
  const suggestion = view.preview ? null : specSuggestionOf(layout);
  const switchSpec = readOnly ? undefined : onUseAlternative;
  const shortlistedCount = pickedSupplierCount(suppliers, tickedKeys);
  const shortlistedNames = main.filter((g) => g.listings.some((s) => tickedKeys.includes(listingKeyOf(s)))).map((g) => g.name);
  const emphasis = new Set(alternatives.flatMap((a) => a.changes.map((c) => c.key)));

  const supplierContext = { quantity, targetPrice, leadWindowWeeks, requiredCompliance, partLabel: shortPartName(name) };
  const waitLine = detailWaitLine({ researchState, phase, attempt, maxAttempts, startsInMinutes });
  const lineChip = chips.find((c) => c.isLine);

  return (
    <div className="flex min-w-0 flex-col gap-s4 overflow-y-auto px-[14px] py-[12px] max-md:min-h-[52vh]">
      <div className="shrink-0 px-[2px]">
        <div className="flex flex-wrap items-baseline gap-x-s3 gap-y-s1">
          <span className="text-[14px] font-bold text-ink">{name}</span>
          {phase === 'ready' && shortlistedCount > 0 && (
            <span className="text-meta font-semibold text-success-ink">{shortlistedCount} shortlisted</span>
          )}
        </div>
        <LineSpecSummary
          partNumber={partNumber}
          mpn={mpn}
          keySpecs={keySpecs}
          emphasis={emphasis}
          description={description}
          quantity={quantity}
        />
      </div>

      {waitLine && (
        <ResearchStages
          line={waitLine}
          stage={researchStage}
          knownSuppliers={knownSuppliers}
          busy={researchState ? researchState !== 'QUEUED' : phase === 'searching'}
          compact={suppliers.length > 0}
        />
      )}

      {phase === 'failed' && (
        <CenteredState>
          <span className="flex h-10 w-10 items-center justify-center rounded-round bg-danger-bg">
            <AlertTriangle className="h-5 w-5 text-danger-ink" />
          </span>
          <div>
            <p className="m-0 text-row-title text-danger-ink">Research failed for this component</p>
            <p className="m-0 mt-s1 max-w-md text-body-sm text-ink-secondary">
              {readOnly
                ? `I hit an error while researching ${name}. Re-running it is the owner's to do — the other components are unaffected.`
                : `I hit an error while researching ${name}. Run it again — your other components are unaffected.`}
            </p>
          </div>
          {!readOnly && (
            <Button
              variant="secondary"
              onClick={onRetry}
              loading={isRetrying}
              loadingLabel="Retrying…"
              iconLeft={<RotateCw />}
            >
              Retry research
            </Button>
          )}
        </CenteredState>
      )}

      {phase === 'empty' && (
        <CenteredState>
          <span className="flex h-10 w-10 items-center justify-center rounded-round bg-neutral-bg">
            <Search className="h-5 w-5 text-ink-muted" />
          </span>
          <div>
            <p className="m-0 text-row-title text-ink">No suppliers found</p>
            <p className="m-0 mt-s1 max-w-md text-body-sm text-ink-secondary">
              {readOnly
                ? `Research finished without reliable suppliers for ${name} under the current criteria.`
                : `Research finished without reliable suppliers for ${name} under your current criteria. Re-run it, or adjust your sourcing criteria and try again.`}
            </p>
          </div>
          {!readOnly && (
            <Button
              variant="secondary"
              onClick={onRetry}
              loading={isRetrying}
              loadingLabel="Re-running…"
              iconLeft={<RotateCw />}
            >
              Re-run research
            </Button>
          )}
        </CenteredState>
      )}

      {phase === 'ready' && (
        <SpecChips chips={chips} selected={view.specGroup} onSelect={view.choose} />
      )}

      {phase === 'ready' && suggestion && (
        <SpecSuggestion suggestion={suggestion} onSwitch={switchSpec} switching={switchingSpec} />
      )}

      {phase === 'ready' && view.preview && (
        <SpecPreviewBar
          specGroup={view.preview.specGroup}
          label={alternativeLabelOf(view.preview)}
          onUse={switchSpec}
          switching={switchingSpec}
        />
      )}

      {phase === 'ready' && !view.preview && main.length === 0 && !suggestion && lineChip && (
        <p className="m-0 shrink-0 px-[2px] text-body-sm text-ink-secondary">No supplier sells {lineChip.label}.</p>
      )}

      {phase === 'ready' && (
        <SpecSupplierList
          key={view.specGroup || 'line'}
          split={view.preview ?? layout}
          preview={Boolean(view.preview)}
          tickedKeys={tickedKeys}
          onToggleListing={onToggleListing}
          onAddToFirstMessage={readOnly ? undefined : onAddToFirstMessage}
          {...supplierContext}
        />
      )}

      {phase === 'ready' && !view.preview && main.length > 0 && (
        <SupplierTrustMatrix suppliers={main.map((g) => g.best)} shortlistedNames={shortlistedNames} targetPrice={targetPrice} />
      )}
    </div>
  );
};

export default ComponentDetailPane;
